import { Model } from './Model'
import api from '@/api'

let _requestCounter = 0
const nextRequestId = () => `req-${++_requestCounter}`

export class RestModel extends Model {
  _endpoint = null
  _isBusy = false
  _requestsInProgress = {}

  constructor(data = {}, config = {}) {
    super(data, config)
    if (config.endpoint) this._endpoint = config.endpoint
  }

  // --- Request tracking ---

  get isBusy() {
    return this._isBusy
  }

  hasRequestsInProgress(method) {
    if (method) return Object.values(this._requestsInProgress).includes(method)
    return Object.keys(this._requestsInProgress).length > 0
  }

  _startRequest(method) {
    const id = nextRequestId()
    this._requestsInProgress[id] = method
    this._isBusy = true
    return id
  }

  _endRequest(id) {
    delete this._requestsInProgress[id]
    this._isBusy = this.hasRequestsInProgress()
  }

  // --- Endpoint ---

  endpoint(options = {}) {
    const { id, autoAppendId = true, append = '', params: endpointParams = {} } = options

    let ep = typeof this._endpoint === 'function' ? this._endpoint(options) : this._endpoint

    // Replace :param placeholders
    const placeholders = [...ep.matchAll(/:([A-Za-z]+)/g)]
    for (const match of placeholders) {
      const attr = match[1]
      const value = endpointParams[attr] ?? this.get(attr)
      if (value !== null && value !== undefined) {
        ep = ep.replace(':' + attr, value)
      }
    }

    if (autoAppendId && id != null) ep += '/' + id
    if (append) ep += append

    return ep
  }

  // --- CRUD ---

  async find(options = {}) {
    const { params, asPlainData = false, limit, page } = options
    const requestId = this._startRequest('get')
    const paginated = limit != null || page != null

    try {
      const ep = this.endpoint(options)
      const headers = {}
      if (limit != null) headers['X-Per-Page'] = limit
      if (page != null) headers['X-Page'] = page
      const response = await api.get(ep, { params, headers })
      const data = response.data
      const total =
        paginated && response.headers['x-total-count'] != null ? Number(response.headers['x-total-count']) : undefined

      if (asPlainData) {
        return total !== undefined ? { data, total } : data
      }
      const models = this._toModels(data)
      return total !== undefined ? { data: models, total } : models
    } finally {
      this._endRequest(requestId)
    }
  }

  async findOne(id, options = {}) {
    if (!id) throw new Error('id is required for findOne')

    const { params, asPlainData = false } = options
    const requestId = this._startRequest('get')

    try {
      const ep = this.endpoint({ ...options, id })
      const response = await api.get(ep, { params })
      const data = response.data

      if (asPlainData) return data
      return this._toModel(data)
    } finally {
      this._endRequest(requestId)
    }
  }

  async save(options = {}) {
    return this.isNew() ? this.create(options) : this.update(options)
  }

  async create(options = {}) {
    let { payload, asPlainData = false, applyResponseData = false } = options

    if (!payload) payload = this.getData()

    const pk = this.primaryKey()
    if (pk && pk in payload) delete payload[pk]

    const requestId = this._startRequest('post')

    try {
      const ep = this.endpoint(options)
      const response = await api.post(ep, payload)
      const data = response.data

      if (pk && data[pk]) this.set(pk, data[pk], false)
      this.clearChanged()

      if (applyResponseData) this.setAttributes(data, false)

      if (asPlainData) return data
      return this._toModel(data)
    } finally {
      this._endRequest(requestId)
    }
  }

  async update(options = {}) {
    let { payload, method = 'put', asPlainData = false, applyResponseData = false } = options

    if (!payload) payload = this.getChanged() || this.getData()

    const pk = this.primaryKey()
    if (pk && pk in payload) delete payload[pk]

    const requestId = this._startRequest(method)

    try {
      const ep = this.endpoint({ ...options, id: this.getPrimary() })
      const response = await api[method](ep, payload)
      const data = response.data

      this.clearChanged()

      if (applyResponseData) this.setAttributes(data, false)

      if (asPlainData) return data
      return this._toModel(data)
    } finally {
      this._endRequest(requestId)
    }
  }

  async patch(options = {}) {
    return this.update({ ...options, method: 'patch' })
  }

  async delete(options = {}) {
    const id = options.id || this.getPrimary()
    if (!id) {
      console.warn('Trying to delete model without id')
      return false
    }

    const requestId = this._startRequest('delete')

    try {
      const ep = this.endpoint({ ...options, id })
      const result = await api.delete(ep)
      return result.data
    } finally {
      this._endRequest(requestId)
    }
  }

  // --- Helpers ---

  _toModel(data) {
    if (!data || typeof data !== 'object') return data
    return new this.constructor(data)
  }

  _toModels(data) {
    if (Array.isArray(data)) return data.map((item) => new this.constructor(item))
    return this._toModel(data)
  }
}

export default RestModel
